"use client";

import type { Media } from "@mw/shared/payload-types";

import { Image } from "@components/core/Image";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import { useEffect, useMemo, useRef, useState } from "react";

export interface VideoSlide {
  badge?: null | string;
  description?: null | string;
  icon?: Media | null | string;
  id?: null | string;
  title?: null | string;
  video?: Media | null | string;
  videoPlaceholder?: Media | null | string;
}

export interface InfiniteRotatorSlide {
  type?: "video" | null;
  videoSlide?: VideoSlide | null;
}

export interface InfiniteRotatorConfig {
  skew?: boolean | null;
  slides?: InfiniteRotatorSlide[] | null;
}
interface InfiniteRotatorProps {
  className?: string;
  config: InfiniteRotatorConfig;
}

const DESKTOP_ROTATE_OFFSET = 2;
const MOBILE_ROTATE_OFFSET = 1;

export function InfiniteRotator({ className, config }: InfiniteRotatorProps) {
  const { slides = [], skew = true } = config;
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<number | undefined>(undefined);
  const [transformStep, setTransformStep] = useState(0);
  const isMobile = useIsMobile();

  const [isResetting, setIsResetting] = useState(false);

  const visibleSlides = useMemo(() => {
    if (!slides || slides.length === 0) return [];

    // For continuous slideshow: duplicate first 3 slides at the end
    // This allows seamless looping when we reach the end
    const originalSlides = slides.filter(Boolean);
    const firstThreeSlides = originalSlides.slice(0, 3);
    return [...originalSlides, ...firstThreeSlides];
  }, [slides]);

  const originalSlidesCount = slides?.length || 0;

  const handleReset = () => {
    return new Promise(resolve => {
      setIsResetting(true);
      runAfterNextBrowserPaint(() => {
        setTransformStep(0);
        runAfterNextBrowserPaint(() => {
          setIsResetting(false);
          resolve(true);
        });
      });
    });
  };

  useEffect(() => {
    if (isMobile) {
      setTransformStep(0);
      setCurrentStep(0);
    } else {
      setTransformStep(0);
      setCurrentStep(1);
    }
  }, [isMobile]);

  const handleVideoEnd = async () => {
    if (currentStep === undefined) return;
    // On mobile, we rotate when we cycled to the first slide, on desktop we rotate when we cycled to the second slide
    const isAtRotationPoint = isMobile ? currentStep === originalSlidesCount : currentStep === originalSlidesCount + 1;
    if (isAtRotationPoint) {
      await handleReset();
    }
    setCurrentStep(step => {
      // The rotation offset makes sure we go to the next slide after the rotation point
      const rotationOffset = isAtRotationPoint ? (isMobile ? MOBILE_ROTATE_OFFSET : DESKTOP_ROTATE_OFFSET) : 0;
      const nextStep = (step ?? 0) + 1;
      if (isMobile) {
        // On mobile, we start from the first slide and don't go further than one extra slide at the end
        return Math.max(0, (nextStep % (originalSlidesCount + MOBILE_ROTATE_OFFSET)) + rotationOffset);
      }
      // On desktop, we start from the second slide and go until the second extra slide at the end
      return Math.max(1, (nextStep % (originalSlidesCount + DESKTOP_ROTATE_OFFSET)) + rotationOffset);
    });

    setTransformStep(step => {
      return (step + 1) % ((slides?.length || 0) + 1);
    });
  };

  if (!visibleSlides?.length) return null;

  return (
    <div
      className={cn("relative mx-auto max-w-[1100px] overflow-hidden pb-[6vh] pt-92 lg:pt-[15vh]", className)}
      ref={containerRef}
    >
      <div className='absolute left-0 top-0 z-20 h-full w-20 bg-gradient-to-r from-[var(--page-background-color)] to-transparent md:w-80' />
      <div className='absolute right-0 top-0 z-20 h-full w-20 bg-gradient-to-l from-[var(--page-background-color)] to-transparent md:w-80' />

      <div className='relative rotate-[-2deg]'>
        <div
          className={cn("flex transform-gpu", isResetting ? "" : "transition-transform duration-1000 ease-out")}
          style={{
            transform: `translateX(-${transformStep * (isMobile ? (100 / 12) * 10 : 100 / 3)}%)`, // Move by 100% on mobile, 1/3 on desktop
            transformStyle: "preserve-3d",
          }}
        >
          {visibleSlides.map((slide, index) => (
            <SliderCard
              index={index}
              isActive={index === currentStep}
              isResetting={isResetting}
              key={index}
              onVideoEnd={handleVideoEnd}
              skew={skew ?? false}
              slide={slide.videoSlide}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface SliderCardProps {
  index: number;
  isActive: boolean;
  isResetting: boolean;
  onVideoEnd: () => void;
  skew: boolean;
  slide: VideoSlide | null | undefined;
}

function SliderCard({ index: _index, isActive, onVideoEnd, skew, slide, isResetting }: SliderCardProps) {
  const { title, description, badge, video, videoPlaceholder, icon } = slide || {};
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Handle video play/pause based on active state
  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current
          .play()
          .then(() => {
            setIsVideoPlaying(true);
          })
          .catch(() => {
            // Ignore autoplay errors
          });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  // Handle video end event
  const handleVideoEnd = () => {
    if (isActive) {
      onVideoEnd();
    }
  };

  return (
    <div
      className={cn(
        "relative w-10/12 flex-shrink-0 overflow-hidden md:w-1/3",
        "transition-transform duration-500 ease-in-out",
        isActive ? "-translate-y-5 scale-90 lg:scale-95" : "scale-[80%] opacity-85",
        skew && "skew-x-[-8deg]",
        isResetting && "duration-0",
      )}
      ref={cardRef}
    >
      {/* Video Content */}
      <div className='relative mb-12 aspect-3x2 overflow-hidden rounded-lg'>
        {/* Static gray background for non-playing videos */}
        {!isActive && <div className='absolute inset-0 bg-dark' />}

        {/* Video Placeholder Image */}
        {videoPlaceholder && isActive && !isVideoPlaying && (
          <Image
            alt={title || "Video placeholder"}
            className='absolute inset-0 h-full w-full object-cover'
            resource={videoPlaceholder}
            sizes={ImageSizes.FULL_1_3_ON_LG}
          />
        )}

        {video && (
          <video
            autoPlay={isActive}
            className={cn("effect-opacity h-full w-full object-cover", !isActive && "opacity-0")}
            loop={false}
            muted
            onEnded={handleVideoEnd}
            playsInline
            preload={isActive ? "auto" : "none"}
            ref={videoRef}
            src={typeof video === "string" ? video : video?.url || ""}
          />
        )}
        {/* Badge */}
        {badge && (
          <div className='absolute right-8 top-4 z-10'>
            <span
              className={cn(
                "f-paragraph-04 rounded-full bg-overlay-dark px-[10px] py-4 text-light",
                isActive && "bg-overlay-light text-inverse",
              )}
            >
              {badge}
            </span>
          </div>
        )}
      </div>

      {/* Content below video */}
      <div className={cn("flex items-center gap-8 px-12 opacity-70", isActive ? "" : "text-overlay-darker-plus-2")}>
        {icon && <Image className='h-20 w-20' resource={icon} sizes='20px' />}
        <div>
          {title && (
            <p className={cn("f-paragraph-03 truncate", isActive ? "" : "text-overlay-darker-plus-2")}>{title}</p>
          )}
          {description && (
            <p className={cn("f-paragraph-04 truncate", isActive ? "" : "text-overlay-darker-plus")}>{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
